import * as React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { DataList, PageHeader, StatusPill, type DataListColumn } from "@ozpanel/kit";
import { Button, ConfirmDialog, Section, formatRelative, useAuth, useToast } from "@ozpanel/ui";

// The cross-tenant Redis login queue — beside Port requests, the same shape. An account asks for a login
// with a PROFILE from the platform's catalog (GET /redis/profiles): exactly the keys, channels and commands
// one application needs. Approving creates the login through the one path every login takes; the account
// then reads its password (audited). Refusing needs a reason the account sees.

interface RedisRequest {
  id: number;
  tenant_id: number;
  tenant?: string;
  name: string;
  username: string;
  profile: string;
  app: string;
  purpose: string;
  status: "requested" | "approved" | "denied";
  decided_reason?: string;
  requested_at: string;
  decided_at?: string;
}
interface RedisProfile {
  name: string;
  title: string;
  source?: string;
  keys: string[];
  channels: string[];
  commands: string[];
  exclusive: boolean;
}

export function RedisRequestsScreen() {
  const { client } = useAuth();
  const qc = useQueryClient();
  const { notify } = useToast();
  const q = useQuery({
    queryKey: ["redis-requests"],
    queryFn: () => client.apiFetch<{ items: RedisRequest[]; pending: number }>(`/redis/requests`),
    refetchInterval: 15000,
  });
  const profiles = useQuery({
    queryKey: ["redis-profiles"],
    queryFn: () => client.apiFetch<{ profiles: RedisProfile[] }>(`/redis/profiles`),
  });
  const [deciding, setDeciding] = React.useState<{ req: RedisRequest; approve: boolean } | null>(null);
  const [reason, setReason] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const items = q.data?.items ?? [];
  const profileOf = (n: string) => profiles.data?.profiles.find((p) => p.name === n);

  const decide = async () => {
    if (!deciding) return;
    setBusy(true);
    try {
      await client.apiFetch(`/redis/requests/${deciding.req.id}/${deciding.approve ? "approve" : "deny"}`, {
        method: "POST",
        body: JSON.stringify({ reason: reason.trim() }),
      });
      notify({ kind: "success", title: `${deciding.req.username} ${deciding.approve ? "approved — login created" : "refused"}` });
      setDeciding(null);
      setReason("");
      void qc.invalidateQueries({ queryKey: ["redis-requests"] });
    } catch (e) {
      notify({ kind: "error", title: "The decision failed", body: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  };

  const cols: DataListColumn<RedisRequest>[] = [
    { key: "tenant", label: "Account", render: (x) => `${x.tenant || "—"} (${x.tenant_id})` },
    { key: "username", label: "Login", render: (x) => <span className="font-mono text-[12.5px]">{x.username}</span> },
    { key: "profile", label: "Profile", render: (x) => <span className="font-mono text-[12px]">{x.profile}</span> },
    { key: "purpose", label: "For", render: (x) => (x.app ? `${x.app} — ${x.purpose}` : x.purpose) },
    {
      key: "status",
      label: "Status",
      render: (x) => (
        <span className="inline-flex flex-col">
          <StatusPill tone={x.status === "approved" ? "ok" : x.status === "denied" ? "bad" : "warn"}>{x.status}</StatusPill>
          {x.decided_reason && <span className="mt-1 text-[11.5px] text-muted">{x.decided_reason}</span>}
        </span>
      ),
    },
    { key: "requested_at", label: "Asked", render: (x) => formatRelative(x.requested_at) },
  ];

  const p = deciding ? profileOf(deciding.req.profile) : undefined;
  return (
    <div>
      <PageHeader title="Redis requests" subtitle="Accounts asking for a Redis login with a profile — approve to create it" />
      <Section title="Requests" hint={q.data ? `${q.data.pending} waiting` : undefined} bodyClassName="p-0">
        <div className="p-4">
          <DataList
            items={items}
            columns={cols}
            getRowKey={(x) => String(x.id)}
            loading={q.isLoading}
            error={q.error}
            errorContext="the Redis login requests"
            emptyMessage="No requests."
            rowActions={(x) =>
              x.status === "requested" ? (
                <div className="flex gap-2 text-[12px]">
                  <Button size="sm" variant="ghost" onClick={() => setDeciding({ req: x, approve: true })}>Approve</Button>
                  <Button size="sm" variant="danger" onClick={() => setDeciding({ req: x, approve: false })}>Refuse</Button>
                </div>
              ) : null
            }
          />
        </div>
      </Section>
      <ConfirmDialog
        open={!!deciding}
        onOpenChange={(o) => !o && setDeciding(null)}
        title={deciding ? `${deciding.approve ? "Approve" : "Refuse"} ${deciding.req.username}` : ""}
        confirmLabel={deciding?.approve ? "Approve and create" : "Refuse"}
        confirmDisabled={!deciding?.approve && reason.trim() === ""}
        pending={busy}
        onConfirm={() => void decide()}
        body={
          <div className="space-y-3 text-[12.5px]">
            {deciding && (
              <div>
                {deciding.req.tenant} ({deciding.req.tenant_id}) asks for <b className="font-mono">{deciding.req.username}</b> with
                profile <b className="font-mono">{deciding.req.profile}</b> — {deciding.req.purpose}
              </div>
            )}
            {p && (
              <div className="space-y-2">
                <div className="text-[12px]">{p.title}</div>
                {(["keys", "channels", "commands"] as const).map((k) => (
                  <div key={k}>
                    <div className="text-[11.5px] capitalize text-muted">{k}</div>
                    <div className="mt-1 max-h-24 overflow-auto rounded border border-line2 bg-bg2 p-2 font-mono text-[11px]">
                      {p[k].length ? p[k].join("  ") : "—"}
                    </div>
                  </div>
                ))}
                {p.source && <div className="text-[11px] text-muted">Derived from {p.source}</div>}
              </div>
            )}
            <label className="block">
              Reason {deciding?.approve ? "(optional)" : "(the account sees it)"}
              <input value={reason} onChange={(e) => setReason(e.target.value)}
                className="w-full rounded border border-line2 bg-bg px-3 py-2 text-[13px] outline-none focus:border-accent" />
            </label>
            {deciding?.approve && (
              <div className="text-[11.5px] text-muted">The login is created now; the account reads its password with Show password.</div>
            )}
          </div>
        }
      />
    </div>
  );
}
